/**
 * Création et modification d'une carte (design system, EcranEditeur) : paquet de destination,
 * type et mode, champs ; « Ajouter la carte » (⌘ Entrée). Type, mode et paquet sont retenus
 * pour la carte suivante.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clesCartesFilles, type CarteMere, type TypeCarte } from '../../cartes/carte-mere';
import { ErreurValidation } from '../../supabase/depot';
import { Bouton } from '../composants/Bouton';
import { Chargement } from '../composants/Chargement';
import { Dialogue } from '../composants/Dialogue';
import { EtatVide } from '../composants/EtatVide';
import { ListeDeroulante } from '../composants/ListeDeroulante';
import { useNotifier } from '../composants/Notifications';
import { Touche } from '../composants/Touche';
import { Coquille } from '../Coquille';
import { aplatir, construireArbre } from '../donnees/arbre';
import { useDonnees } from '../donnees/contexte';
import { messageErreur } from '../donnees/erreurs';
import { useImages } from '../donnees/images';
import type { EditeurPoignee } from '../editeur/Editeur';
import {
  brouillonDepuisCarte,
  brouillonVide,
  carteDuBrouillon,
  erreursDuBrouillon,
  FormulaireCarte,
  type Brouillon,
  type ErreursCarte,
} from '../editeur/FormulaireCarte';
import { naviguer } from '../routes';

const CLE_PAQUET = 'flashcard-dernier-paquet';
const CLE_TYPE = 'flashcard-dernier-type';

function lire(cle: string): string | null {
  try {
    return window.localStorage.getItem(cle);
  } catch {
    return null;
  }
}
function ecrire(cle: string, valeur: string) {
  try {
    window.localStorage.setItem(cle, valeur);
  } catch {
    // stockage indisponible
  }
}

export function EcranEditeur({
  carteMereId,
  paquetDemande,
}: {
  carteMereId?: string;
  paquetDemande?: string;
}) {
  const { depot, paquets, preferences, rechargerPreferences, rechargerJours } = useDonnees();
  const notifier = useNotifier();
  const images = useImages();
  const formulaire = useRef<EditeurPoignee>(null);
  const noeuds = useMemo(() => aplatir(construireArbre(paquets ?? [])), [paquets]);
  const [paquet, setPaquet] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [carteChargee, setCarteChargee] = useState<CarteMere | null>(null);
  const [erreurs, setErreurs] = useState<ErreursCarte>({});
  const [envoi, setEnvoi] = useState(false);
  const [suppression, setSuppression] = useState(false);
  const modification = carteMereId !== undefined;

  // Paquet de destination : demandé, sinon le dernier utilisé, sinon le premier.
  useEffect(() => {
    if (modification || paquets === null || paquet !== null) return;
    const ids = new Set(paquets.map((p) => p.id));
    const dernier = lire(CLE_PAQUET);
    const choix =
      (paquetDemande && ids.has(paquetDemande) ? paquetDemande : null) ??
      (dernier && ids.has(dernier) ? dernier : null) ??
      noeuds[0]?.paquet.id ??
      null;
    setPaquet(choix);
  }, [modification, paquets, paquetDemande, noeuds, paquet]);

  // Brouillon : carte à modifier, ou carte vide avec les derniers choix du compte.
  useEffect(() => {
    if (modification) {
      let actif = true;
      void depot
        .carteMere(carteMereId)
        .then((lue) => {
          if (!actif) return;
          setCarteChargee(lue.carte);
          setPaquet(lue.paquetId);
          setBrouillon(brouillonDepuisCarte(lue.carte, preferences?.derniersModes));
        })
        .catch((e) => notifier({ message: messageErreur(e), statut: 'danger' }));
      return () => {
        actif = false;
      };
    }
    if (preferences !== null && brouillon === null) {
      const type = (lire(CLE_TYPE) as TypeCarte | null) ?? 'basique';
      setBrouillon(
        brouillonVide(
          ['basique', 'trous', 'image', 'tableau'].includes(type) ? type : 'basique',
          preferences.derniersModes,
        ),
      );
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modification, carteMereId, preferences === null]);

  const changer = useCallback((b: Brouillon) => {
    setBrouillon(b);
    setErreurs((actuelles) =>
      Object.keys(actuelles).length > 0 ? erreursDuBrouillon(b) : actuelles,
    );
  }, []);

  async function enregistrer() {
    if (!brouillon || !paquet || envoi) return;
    const trouvees = erreursDuBrouillon(brouillon);
    setErreurs(trouvees);
    if (Object.keys(trouvees).length > 0) return;
    setEnvoi(true);
    try {
      await depot.enregistrerCarteMere(carteMereId ?? null, paquet, carteDuBrouillon(brouillon));
      ecrire(CLE_PAQUET, paquet);
      ecrire(CLE_TYPE, brouillon.type);
      void rechargerPreferences();
      void rechargerJours();
      const nom = noeuds.find((n) => n.paquet.id === paquet)?.paquet.nom ?? '';
      if (modification) {
        notifier({ message: 'Carte enregistrée.', statut: 'succes' });
        window.history.length > 1 ? window.history.back() : naviguer({ ecran: 'gestion' });
      } else {
        notifier({
          message: `Carte ajoutée à ${nom}.`,
          cle: 'carte-ajoutee',
          statut: 'succes',
          action: { libelle: 'Afficher', onAction: () => naviguer({ ecran: 'gestion', paquet }) },
        });
        const vide = brouillonVide(brouillon.type, brouillon.modes);
        setBrouillon(vide);
        setErreurs({});
        window.setTimeout(() => formulaire.current?.focus(), 0);
      }
    } catch (e) {
      if (e instanceof ErreurValidation) setErreurs({ masquage: e.message });
      else notifier({ message: messageErreur(e), statut: 'danger' });
    } finally {
      setEnvoi(false);
    }
  }

  const titre = modification ? 'Modifier la carte' : 'Nouvelle carte';

  if (paquets !== null && noeuds.length === 0) {
    return (
      <Coquille titre={titre}>
        <EtatVide
          icone="layers"
          titre="Aucun paquet"
          texte="Une carte se range dans un paquet : créez d’abord un paquet principal."
          action={
            <Bouton
              variante="principal"
              icone="folder-plus"
              onClick={() => naviguer({ ecran: 'paquets' })}
            >
              Aller aux paquets
            </Bouton>
          }
          style={{ paddingTop: 'var(--espace-48)' }}
        />
      </Coquille>
    );
  }

  return (
    <Coquille titre={titre}>
      {brouillon === null || paquets === null ? (
        <Chargement type="rotation" texte="Chargement de la carte…" centre />
      ) : (
        <>
          <div className="fc-fenetre__entete">
            <h2 className="fc-fenetre__titre-ecran">{titre}</h2>
          </div>
          <div className="fc-ecran-editeur">
            <div className="fc-ecran-editeur__destination">
              <span className="fc-champ-groupe__libelle" id="editeur-destination">
                Paquet
              </span>
              <ListeDeroulante
                libelleId="editeur-destination"
                options={noeuds.map((n) => ({
                  valeur: n.paquet.id,
                  libelle: n.paquet.nom,
                  icone: n.niveau === 0 ? 'layers' : 'folder',
                  niveau: n.niveau,
                }))}
                affichage={(o) =>
                  noeuds.find((n) => n.paquet.id === o.valeur)?.chemin.join(' › ') ?? o.libelle
                }
                valeur={paquet}
                onChoisir={setPaquet}
                exemple="Choisir un paquet"
                desactive={envoi}
              />
            </div>
            <FormulaireCarte
              ref={formulaire}
              brouillon={brouillon}
              onChange={changer}
              erreurs={erreurs}
              desactive={envoi}
              {...(carteChargee ? { clesAvant: clesCartesFilles(carteChargee) } : {})}
              onEnregistrer={() => void enregistrer()}
              envoyerImage={images.envoyer}
              resoudreImage={images.resoudre}
              couleursPersonnelles={preferences?.couleursSurlignage ?? []}
              onAjouterCouleur={(couleur) => {
                const actuelles = preferences?.couleursSurlignage ?? [];
                if (actuelles.includes(couleur)) return;
                void depot
                  .modifierPreferences({ couleursSurlignage: [...actuelles, couleur].slice(-50) })
                  .then(rechargerPreferences)
                  .catch((e) => notifier({ message: messageErreur(e), statut: 'danger' }));
              }}
              onErreur={(message) => notifier({ message, statut: 'danger' })}
              barreCollee
            />
            <footer className="fc-ecran-editeur__pied">
              {modification ? (
                <Bouton
                  variante="danger-discret"
                  icone="trash-2"
                  onClick={() => setSuppression(true)}
                  desactive={envoi}
                >
                  Supprimer la carte
                </Bouton>
              ) : (
                <p className="fc-ecran-editeur__memo">
                  Type, mode et paquet sont retenus pour la carte suivante.
                </p>
              )}
              <div className="fc-ecran-editeur__actions">
                {modification && (
                  <Bouton onClick={() => window.history.back()} desactive={envoi}>
                    Annuler
                  </Bouton>
                )}
                <Bouton
                  variante="principal"
                  occupe={envoi}
                  aria-keyshortcuts="Meta+Enter"
                  onClick={() => void enregistrer()}
                >
                  {modification ? 'Enregistrer' : 'Ajouter la carte'}
                  <Touche>⌘ Entrée</Touche>
                </Bouton>
              </div>
            </footer>
          </div>
        </>
      )}
      {suppression && carteMereId && (
        <Dialogue
          titre="Supprimer la carte ?"
          texte={
            carteChargee && clesCartesFilles(carteChargee).length > 1
              ? `Ses ${clesCartesFilles(carteChargee).length} cartes filles et leur historique de révision seront supprimés. Cette action est définitive.`
              : 'La carte et son historique de révision seront supprimés. Cette action est définitive.'
          }
          actionPrincipale={{
            libelle: 'Supprimer la carte',
            variante: 'danger',
            onAction: async () => {
              try {
                await depot.supprimerCarteMere(carteMereId);
                void rechargerJours();
                notifier({ message: 'Carte supprimée.', statut: 'succes' });
                naviguer({ ecran: 'gestion' }, true);
              } catch (e) {
                notifier({ message: messageErreur(e), statut: 'danger' });
                setSuppression(false);
              }
            },
          }}
          onFermer={() => setSuppression(false)}
        />
      )}
    </Coquille>
  );
}
